import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./useAuth";

export interface EarningsBreakdown {
  /** Gains issus de coupons gagnants (type=winning hors gift) */
  couponWins: number;
  /** Bonus parrainage (type=bonus) */
  referralBonus: number;
  /** Cagnotte Gift distribuée (type=winning avec description contenant "Gift") */
  giftPool: number;
  /** Total = couponWins + referralBonus + giftPool */
  total: number;
  isLoading: boolean;
}

/**
 * Stats de gains calculées depuis la table `transactions` — source unique
 * de vérité, contrairement au champ `users.total_earnings` qui n'est pas
 * mis à jour de manière fiable par les edge functions.
 */
export function useEarnings(): EarningsBreakdown {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["earnings", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("type, amount, description")
        .eq("user_id", user!.id)
        .in("type", ["winning", "bonus"])
        .eq("status", "success");
      if (error) throw error;
      return (data ?? []) as Array<{ type: string; amount: number; description: string | null }>;
    },
  });

  const rows = q.data ?? [];
  let couponWins = 0;
  let referralBonus = 0;
  let giftPool = 0;
  for (const r of rows) {
    if (r.type === "bonus") {
      referralBonus += r.amount;
    } else if (r.type === "winning") {
      // Gift pool transactions ont une description contenant "Gift"
      if ((r.description ?? "").toLowerCase().includes("gift")) {
        giftPool += r.amount;
      } else {
        couponWins += r.amount;
      }
    }
  }

  return {
    couponWins,
    referralBonus,
    giftPool,
    total: couponWins + referralBonus + giftPool,
    isLoading: q.isLoading,
  };
}
